import React from 'react';
import { Box, GlobalStyles } from '@mui/material';

/**
 * Animated, product-themed backdrop for the login screen — the same idea as the
 * marketing site but denser and richer. It floats live observability artifacts:
 * a service-map topology with pulses travelling the edges, a radar sweep
 * (synthetic / network pings), streaming logs, a latency heatmap, an AI-eval
 * scorecard, plus line/bar charts, trace waterfalls, SLO gauges, sparklines and
 * code. On-brand blue→green over GitHub-dark, low-opacity with a centre vignette
 * so the sign-in card stays crisp. Fully disabled under prefers-reduced-motion.
 */
const BLUE = '#3b82f6';
const GREEN = '#22c55e';
const TEAL = '#14b8a6';
const PURPLE = '#a78bfa';

/* ── glyphs ── */
const ServiceMap = () => (
  <svg viewBox="0 0 220 150" width="100%" height="100%">
    {/* edges */}
    {[['60,40','140,30'],['60,40','40,110'],['140,30','170,100'],['40,110','120,120'],
      ['170,100','120,120'],['140,30','60,40']].map(([a, b], i) => {
      const [x1, y1] = a.split(','); const [x2, y2] = b.split(',');
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={BLUE} strokeOpacity="0.35" strokeWidth="1.5" />;
    })}
    {/* travelling pulses */}
    {[['M60 40 L140 30', BLUE, '0s'], ['M40 110 L120 120', GREEN, '1.2s'], ['M140 30 L170 100', TEAL, '0.6s']].map(([d, c, delay], i) => (
      <circle key={i} r="3" fill={c}>
        <animateMotion dur="2.6s" begin={delay} repeatCount="indefinite" path={d} />
      </circle>
    ))}
    {/* nodes */}
    {[[60,40,BLUE],[140,30,GREEN],[40,110,TEAL],[170,100,BLUE],[120,120,GREEN]].map(([x,y,c],i) => (
      <g key={i}>
        <circle className="lb-node" cx={x} cy={y} r="9" fill={c} fillOpacity="0.18" style={{ animationDelay: `${i*0.4}s` }} />
        <circle cx={x} cy={y} r="5" fill={c} fillOpacity="0.9" />
      </g>
    ))}
  </svg>
);

const Radar = () => (
  <Box sx={{ position: 'relative', width: '100%', paddingTop: '100%' }}>
    <svg viewBox="0 0 120 120" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
      {[18, 36, 54].map((r) => <circle key={r} cx="60" cy="60" r={r} fill="none" stroke={GREEN} strokeOpacity="0.2" />)}
      <line x1="6" y1="60" x2="114" y2="60" stroke={GREEN} strokeOpacity="0.15" />
      <line x1="60" y1="6" x2="60" y2="114" stroke={GREEN} strokeOpacity="0.15" />
      {[[78,46],[44,40],[88,78],[40,82]].map(([x,y],i) => (
        <circle key={i} className="lb-blip" cx={x} cy={y} r="3" fill={GREEN} style={{ animationDelay: `${i*0.7}s` }} />
      ))}
    </svg>
    <Box className="lb-spin" sx={{ position: 'absolute', inset: 0, borderRadius: '50%',
      background: `conic-gradient(from 0deg, transparent 0deg, ${GREEN}44 40deg, transparent 80deg)` }} />
  </Box>
);

const LineArea = ({ c1 = BLUE, c2 = GREEN }) => (
  <svg viewBox="0 0 200 90" width="100%" height="100%">
    <defs><linearGradient id="lb-area" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={c1} stopOpacity="0.35" /><stop offset="100%" stopColor={c1} stopOpacity="0" />
    </linearGradient></defs>
    {[22, 44, 66].map((y) => <line key={y} x1="6" x2="194" y1={y} y2={y} stroke="#fff" strokeOpacity="0.06" />)}
    <path d="M6 70 L34 58 L62 64 L90 40 L118 50 L146 26 L174 38 L194 20 L194 84 L6 84 Z" fill="url(#lb-area)" />
    <path className="lb-draw" d="M6 70 L34 58 L62 64 L90 40 L118 50 L146 26 L174 38 L194 20"
      fill="none" stroke={c2} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle className="lb-pulse" cx="194" cy="20" r="3.5" fill={c2} />
  </svg>
);

const Bars = () => (
  <svg viewBox="0 0 200 90" width="100%" height="100%">
    <line x1="6" x2="194" y1="82" y2="82" stroke="#fff" strokeOpacity="0.12" />
    {[42,64,30,78,52,88,60,72].map((h,i) => (
      <rect key={i} className="lb-bar" x={10+i*23} y={82-h} width="14" height={h} rx="2.5"
        fill={i%2?GREEN:BLUE} fillOpacity="0.8" style={{ transformOrigin:'0 82px', animationDelay:`${i*0.1}s` }} />
    ))}
  </svg>
);

const Trace = () => {
  const spans = [['api-gateway',6,186,BLUE],['auth',22,90,GREEN],['cart',40,80,BLUE],
    ['postgres',58,54,PURPLE],['payment',96,60,GREEN]];
  return (
    <svg viewBox="0 0 200 96" width="100%" height="100%">
      {spans.map(([t,x,w,c],i) => (
        <g key={i}>
          <rect className="lb-span" x={x} y={8+i*17} width={w} height="9" rx="4.5" fill={c} fillOpacity="0.85"
            style={{ animationDelay:`${i*0.14}s` }} />
        </g>
      ))}
    </svg>
  );
};

const Logs = () => {
  const rows = [GREEN, BLUE, '#9aa6b8', GREEN, '#f59e0b', BLUE, '#9aa6b8', GREEN];
  return (
    <svg viewBox="0 0 200 120" width="100%" height="100%">
      <g className="lb-logscroll">
        {rows.concat(rows).map((c, i) => (
          <g key={i} className="lb-logrow" style={{ animationDelay: `${(i%rows.length)*0.25}s` }}>
            <circle cx="8" cy={10+i*15} r="2.5" fill={c} />
            <rect x="16" y={6+i*15} width={40+((i*37)%120)} height="8" rx="2" fill={c} fillOpacity="0.45" />
          </g>
        ))}
      </g>
    </svg>
  );
};

const Heatmap = () => {
  const cols = 12, rowsN = 6;
  return (
    <svg viewBox="0 0 200 100" width="100%" height="100%">
      {Array.from({ length: cols*rowsN }).map((_, i) => {
        const x = (i % cols) * 16 + 4, y = Math.floor(i / cols) * 16 + 4;
        const c = (i * 53) % 3 === 0 ? GREEN : (i * 17) % 4 === 0 ? '#f59e0b' : BLUE;
        return <rect key={i} className="lb-twinkle" x={x} y={y} width="13" height="13" rx="2" fill={c}
          style={{ animationDelay: `${((i*131)%200)/100}s` }} />;
      })}
    </svg>
  );
};

const Gauge = () => (
  <svg viewBox="0 0 100 64" width="100%" height="100%">
    <path d="M12 56 A38 38 0 0 1 88 56" fill="none" stroke="#fff" strokeOpacity="0.12" strokeWidth="8" strokeLinecap="round" />
    <path className="lb-draw" d="M12 56 A38 38 0 0 1 82 30" fill="none" stroke={GREEN} strokeWidth="8" strokeLinecap="round" />
  </svg>
);

const Spark = ({ c2 = GREEN }) => (
  <svg viewBox="0 0 120 40" width="100%" height="100%">
    <path className="lb-draw" d="M4 30 L20 22 L36 26 L52 12 L68 18 L84 8 L100 16 L116 6"
      fill="none" stroke={c2} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Code = () => {
  const rows = [[10,GREEN],[26,BLUE],[18,'#9aa6b8'],[30,GREEN],[14,BLUE],[22,'#9aa6b8']];
  return (
    <svg viewBox="0 0 200 110" width="100%" height="100%">
      {rows.map(([w,col],i) => (
        <g key={i}>
          <rect x="6" y={10+i*16} width="4" height="9" rx="1.5" fill="#6b7689" fillOpacity="0.5" />
          <rect x="16" y={10+i*16} width={w*5} height="9" rx="2.5" fill={col} fillOpacity="0.7" />
        </g>
      ))}
      <rect className="lb-scan" x="6" y="6" width="188" height="14" rx="3" fill="#fff" fillOpacity="0.06" />
    </svg>
  );
};

const AiScore = () => {
  const rows = [['correctness',0.96,GREEN],['faithfulness',0.94,GREEN],['relevance',0.88,BLUE],['safety',0.99,GREEN]];
  return (
    <svg viewBox="0 0 200 110" width="100%" height="100%">
      {rows.map(([t,v,c],i) => (
        <g key={i}>
          <rect x="6" y={10+i*24} width="120" height="8" rx="4" fill="#fff" fillOpacity="0.08" />
          <rect className="lb-grow" x="6" y={10+i*24} width={120*v} height="8" rx="4" fill={c}
            style={{ transformOrigin:'6px 0', animationDelay:`${i*0.18}s` }} />
          <circle cx="190" cy={14+i*24} r="6" fill={c} fillOpacity="0.18" />
          <path d={`M186 ${14+i*24} l3 3 l6 -7`} fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      ))}
    </svg>
  );
};

const PulseRing = ({ c = '#f59e0b' }) => (
  <Box sx={{ position: 'relative', width: '100%', paddingTop: '100%' }}>
    {[0, 1, 2].map((i) => (
      <Box key={i} className="lb-ring" sx={{ position: 'absolute', inset: 0, borderRadius: '50%',
        border: `2px solid ${c}`, animationDelay: `${i * 0.8}s` }} />
    ))}
    <Box sx={{ position: 'absolute', inset: '40%', borderRadius: '50%', background: c, opacity: 0.9 }} />
  </Box>
);

const GLYPHS = { ServiceMap, Radar, LineArea, Bars, Trace, Logs, Heatmap, Gauge, Spark, Code, AiScore, PulseRing };

// Dense placement, kept away from the centred card; some hidden on small screens.
const FIELD = [
  { type: 'ServiceMap', top: '6%',  left: '3%',  w: 280, dur: 26, delay: 0,   op: 0.30 },
  { type: 'Radar',      top: '10%', left: '80%', w: 150, dur: 30, delay: -5,  op: 0.30 },
  { type: 'LineArea',   top: '60%', left: '2%',  w: 240, dur: 28, delay: -3,  op: 0.26 },
  { type: 'Trace',      top: '70%', left: '74%', w: 250, dur: 31, delay: -8,  op: 0.24 },
  { type: 'Logs',       top: '38%', left: '85%', w: 180, dur: 27, delay: -2,  op: 0.22, sm: false },
  { type: 'Heatmap',    top: '40%', left: '1%',  w: 200, dur: 33, delay: -10, op: 0.20, sm: false },
  { type: 'AiScore',    top: '8%',  left: '40%', w: 210, dur: 25, delay: -6,  op: 0.20, sm: false },
  { type: 'Bars',       top: '82%', left: '40%', w: 200, dur: 29, delay: -4,  op: 0.22 },
  { type: 'Gauge',      top: '60%', left: '46%', w: 130, dur: 24, delay: -1,  op: 0.22, sm: false },
  { type: 'Spark',      top: '26%', left: '12%', w: 150, dur: 23, delay: -7,  op: 0.18, sm: false },
  { type: 'Code',       top: '78%', left: '20%', w: 190, dur: 32, delay: -12, op: 0.18, sm: false },
  { type: 'PulseRing',  top: '24%', left: '66%', w: 70,  dur: 22, delay: -3,  op: 0.4 },
  { type: 'PulseRing',  top: '88%', left: '88%', w: 56,  dur: 20, delay: -9,  op: 0.32, sm: false },
];

const LoginBackdrop = () => (
  <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    <GlobalStyles styles={{
      '@keyframes lb-float': { '0%,100%': { transform: 'translate3d(0,0,0)' }, '50%': { transform: 'translate3d(0,-24px,0)' } },
      '@keyframes lb-draw': { '0%': { strokeDashoffset: 340 }, '55%,100%': { strokeDashoffset: 0 } },
      '@keyframes lb-bar': { '0%': { transform: 'scaleY(0)' }, '60%,100%': { transform: 'scaleY(1)' } },
      '@keyframes lb-grow': { '0%': { transform: 'scaleX(0)' }, '60%,100%': { transform: 'scaleX(1)' } },
      '@keyframes lb-span': { '0%': { transform: 'translateX(-12px)', opacity: 0 }, '60%,100%': { transform: 'none', opacity: 1 } },
      '@keyframes lb-spin': { '0%': { transform: 'rotate(0)' }, '100%': { transform: 'rotate(360deg)' } },
      '@keyframes lb-twinkle': { '0%,100%': { opacity: 0.12 }, '50%': { opacity: 0.7 } },
      '@keyframes lb-blip': { '0%,100%': { opacity: 0 }, '20%': { opacity: 1 }, '60%': { opacity: 0.2 } },
      '@keyframes lb-logscroll': { '0%': { transform: 'translateY(0)' }, '100%': { transform: 'translateY(-120px)' } },
      '@keyframes lb-ring': { '0%': { transform: 'scale(0.3)', opacity: 0.8 }, '100%': { transform: 'scale(1)', opacity: 0 } },
      '@keyframes lb-node': { '0%,100%': { transform: 'scale(1)', opacity: 0.18 }, '50%': { transform: 'scale(1.5)', opacity: 0.4 } },
      '@keyframes lb-scan': { '0%': { transform: 'translateY(0)', opacity: 0.5 }, '100%': { transform: 'translateY(86px)', opacity: 0 } },
      '@keyframes lb-pulse': { '0%,100%': { r: 3, opacity: 1 }, '50%': { r: 5, opacity: 0.5 } },
      '.lb-draw': { strokeDasharray: 340, animation: 'lb-draw 7s ease-in-out infinite' },
      '.lb-bar': { animation: 'lb-bar 6s ease-in-out infinite' },
      '.lb-grow': { animation: 'lb-grow 6s ease-in-out infinite' },
      '.lb-span': { animation: 'lb-span 6s ease-in-out infinite' },
      '.lb-spin': { animation: 'lb-spin 6s linear infinite', transformOrigin: '50% 50%' },
      '.lb-twinkle': { animation: 'lb-twinkle 3.5s ease-in-out infinite' },
      '.lb-blip': { animation: 'lb-blip 3s ease-in-out infinite' },
      '.lb-logscroll': { animation: 'lb-logscroll 9s linear infinite' },
      '.lb-ring': { animation: 'lb-ring 2.4s ease-out infinite' },
      '.lb-node': { animation: 'lb-node 3s ease-in-out infinite', transformBox: 'fill-box', transformOrigin: 'center' },
      '.lb-scan': { animation: 'lb-scan 5s ease-in-out infinite' },
      '.lb-pulse': { animation: 'lb-pulse 2.4s ease-in-out infinite' },
      '@media (prefers-reduced-motion: reduce)': {
        '[class^="lb-"]': { animation: 'none !important', strokeDashoffset: '0 !important' },
      },
    }} />
    {/* base */}
    <Box sx={{ position: 'absolute', inset: 0, background:
      'radial-gradient(1300px 700px at 50% -10%, #11233f 0%, #0c111a 52%, #0a0d12 100%)' }} />
    {/* dashboard grid */}
    <Box sx={{ position: 'absolute', inset: 0, opacity: 0.12,
      backgroundImage: 'linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)',
      backgroundSize: '46px 46px', maskImage: 'radial-gradient(circle at 50% 40%, #000 0%, transparent 80%)',
      WebkitMaskImage: 'radial-gradient(circle at 50% 40%, #000 0%, transparent 80%)' }} />
    {/* glyph field */}
    {FIELD.map((g, i) => {
      const G = GLYPHS[g.type];
      return (
        <Box key={i} sx={{ position: 'absolute', top: g.top, left: g.left, width: g.w, opacity: g.op,
          animation: `lb-float ${g.dur}s ease-in-out ${g.delay}s infinite`,
          display: { xs: g.sm === false ? 'none' : 'block', md: 'block' },
          filter: 'drop-shadow(0 6px 22px rgba(0,0,0,0.45))' }}>
          <G />
        </Box>
      );
    })}
    {/* centre vignette so the sign-in card pops */}
    <Box sx={{ position: 'absolute', inset: 0, background:
      'radial-gradient(46% 52% at 50% 50%, rgba(10,13,18,0.9) 0%, rgba(10,13,18,0.55) 45%, transparent 80%)' }} />
  </Box>
);

export default LoginBackdrop;
